import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useSystemAdminLoginMutation,
  useSystemAdminInitMutation,
} from '../api/adminApi';
import { logout, setCredentials, setInit } from '../store/authSlice';
import type { AppDispatch, RootState } from '../store/index';

export function useAdminAuth() {
  const dispatch = useDispatch<AppDispatch>();
  const auth = useSelector((state: RootState) => state.auth);
  const [loginMutation, loginState] = useSystemAdminLoginMutation();
  const [initMutation, initState] = useSystemAdminInitMutation();

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await loginMutation({ email, password }).unwrap();
      const payload = result.systemAdminLogin;
      dispatch(
        setCredentials({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          admin: payload.admin,
        }),
      );
      const initResult = await initMutation().unwrap();
      dispatch(
        setInit({
          admin: initResult.systemAdminInit.admin,
          socketRooms: initResult.systemAdminInit.socketRooms,
        }),
      );
      return initResult.systemAdminInit;
    },
    [dispatch, initMutation, loginMutation],
  );

  const signOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  return {
    admin: auth.admin,
    accessToken: auth.accessToken,
    initialized: auth.initialized,
    isAuthenticated: Boolean(auth.accessToken),
    login,
    logout: signOut,
    loginLoading: loginState.isLoading || initState.isLoading,
    loginError: loginState.error ?? initState.error,
  };
}
